import React, { useState } from 'react';
import { Organization } from '../../../../types/organization.types';
import { DataField } from './DataField';
import {
  SectionCard,
  ItemCard,
  FieldGrid,
  ItemGrid,
  ViewLink,
  TagList,
  StatCard,
  SocialLink,
  TextBlock,
  SectionLabel,
} from './SectionComponents';
import { ConfirmationDialog } from '../../../common/dialogs/ConfirmationDialog';
import { useUpdateOnboardingData } from './useUpdateOnboardingData';
import { CheckCircle2 } from 'lucide-react';

interface HeroStat {
  value?: string;
  label?: string;
}

interface FeatureLocation {
  title?: string;
  image?: string;
}

interface Testimonial {
  name?: string;
  role?: string;
  text?: string;
}

interface TeamMember {
  name?: string;
  position?: string;
  phone?: string;
  email?: string;
  license?: string;
  headshot?: string;
  bio?: string;
}

interface SocialLinkItem {
  platform?: string;
  url?: string;
}

interface WebsiteBuilderContent {
  hero?: {
    headline?: string;
    tagline?: string;
    primaryCTA?: string;
    secondaryCTA?: string;
    logo?: string;
    backgroundImage?: string;
    backgroundVideo?: string;
    stats?: HeroStat[];
  };
  about?: {
    companyName?: string;
    teamPhoto?: string;
    teamVideo?: string;
    bio?: string;
  };
  featureLocation?: { locations?: FeatureLocation[] };
  testimonials?: {
    googleReviewsLink?: string;
    useGoogleReviews?: boolean;
    testimonials?: Testimonial[];
  };
  meetTheTeam?: {
    teamRosterLink?: string;
    useTeamRosterLink?: boolean;
    teamMembers?: TeamMember[];
  };
  footer?: {
    brokerageName?: string;
    brokerageLogo?: string;
    copyrightText?: string;
    contactInfo?: { phone?: string; email?: string; address?: string };
    socialLinks?: SocialLinkItem[];
  };
}

interface WebsiteSectionData {
  website_completed?: boolean;
  websiteCompleted?: boolean;
  website_builder?: WebsiteBuilderContent;
  websiteBuilder?: WebsiteBuilderContent;
  is_primary_website?: string;
  has_secondary_website?: string;
  domain_name?: string;
  domain_ownership?: string;
  uses_godaddy?: boolean;
  needs_access_help?: boolean;
  builder_completed?: boolean;
  domain_registrar?: string;
  additional_files_link?: string;
  design_update_emails?: string[];
  registrar_email?: string;
  registrar_username?: string;
  registrar_phone?: string;
  registrar_password?: string;
}

interface OnboardingDataShape {
  website?: WebsiteSectionData;
  [key: string]: unknown;
}

interface WebsiteBrandingSectionProps {
  selectedOrg: Organization;
  onboardingSetup: unknown;
  onboardingData: OnboardingDataShape;
  asTab?: boolean;
  onRefresh?: () => void;
}

export const WebsiteBrandingSection: React.FC<WebsiteBrandingSectionProps> = ({
  selectedOrg,
  onboardingData,
  onRefresh,
}) => {
  const websiteData = onboardingData.website;
  // Accept legacy camelCase `websiteBuilder` from old Firestore onboarding.
  const websiteBuilder = websiteData?.website_builder ?? websiteData?.websiteBuilder;

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { updateWebsiteCompleted, saving } = useUpdateOnboardingData({
    organizationId: selectedOrg?.id,
  });

  // Accept legacy camelCase `websiteCompleted` from old Firestore onboarding.
  const isWebsiteCompleted =
    (websiteData?.website_completed ?? websiteData?.websiteCompleted) === true;

  const handleWebsiteCompletedToggle = () => {
    if (!isWebsiteCompleted) {
      // Show confirmation dialog before marking as completed
      setShowConfirmDialog(true);
    } else {
      // Directly toggle off (no confirmation needed)
      handleConfirmWebsiteCompleted(false);
    }
  };

  const handleConfirmWebsiteCompleted = async (completed: boolean = true) => {
    const success = await updateWebsiteCompleted(completed, onboardingData);
    if (success) {
      onRefresh?.();
    }
    setShowConfirmDialog(false);
  };

  // Early return if no data
  if (!websiteData) {
    return <p className="text-gray-500 dark:text-gray-400 text-sm">No website data available</p>;
  }

  const hasRegistrarInfo =
    websiteData?.registrar_email ||
    websiteData?.registrar_username ||
    websiteData?.registrar_phone ||
    websiteData?.registrar_password;

  const hasNoContent =
    !websiteBuilder && !websiteData?.design_update_emails?.length && !websiteData?.domain_name;

  // Check if user requested a website (primary or secondary)
  const requestedWebsite =
    websiteData?.is_primary_website === 'yes' || websiteData?.has_secondary_website === 'yes';

  return (
    <div className="space-y-4">
      {/* Website Completion Status - Only show if user requested a website */}
      {requestedWebsite && (
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle2
                className={`w-5 h-5 ${isWebsiteCompleted ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}
              />
              <div>
                <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Website Design Status</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {isWebsiteCompleted
                    ? 'Website design has been completed'
                    : 'Website design is in progress'}
                </p>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isWebsiteCompleted}
                onChange={handleWebsiteCompletedToggle}
                disabled={saving}
                className="w-4 h-4 text-blue-600 border-gray-300 dark:border-white/15 rounded focus:ring-blue-500 cursor-pointer"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Mark as Completed</span>
            </label>
          </div>
        </div>
      )}

      {/* Domain Information */}
      <SectionCard title="Website  Information">
        <FieldGrid>
          {websiteData?.domain_name && (
            <DataField label="Domain Name" value={websiteData?.domain_name} type="link" />
          )}
          <DataField
            label="Domain Ownership"
            value={
              websiteData?.domain_ownership === 'own'
                ? 'Already owns domain'
                : websiteData?.domain_ownership === 'purchase'
                  ? 'Purchase requested'
                  : websiteData?.domain_ownership
            }
          />
          <DataField label="Is Primary Website" value={websiteData?.is_primary_website} />
          <DataField label="Has Secondary Website" value={websiteData?.has_secondary_website} />
          <DataField label="Uses GoDaddy" value={websiteData?.uses_godaddy} type="boolean" />
          <DataField
            label="Needs Access Help"
            value={websiteData?.needs_access_help}
            type="boolean"
          />
          <DataField
            label="Builder Completed"
            value={websiteData?.builder_completed}
            type="boolean"
          />
          <DataField label="Domain Registrar" value={websiteData?.domain_registrar} />
          <DataField
            label="Additional Files Link"
            value={websiteData?.additional_files_link}
            type="link"
          />
        </FieldGrid>

        {/* Design Update Emails */}
        {websiteData?.design_update_emails && websiteData.design_update_emails.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100 dark:border-white/10">
            <SectionLabel>Design Update Emails</SectionLabel>
            <TagList items={websiteData.design_update_emails} />
          </div>
        )}
      </SectionCard>

      {/* Registrar Login Info */}
      {hasRegistrarInfo && (
        <SectionCard title="Registrar Login Info">
          <FieldGrid>
            <DataField label="Registrar Email" value={websiteData?.registrar_email} type="email" />
            <DataField label="Registrar Phone" value={websiteData?.registrar_phone} type="phone" />
          </FieldGrid>
          {(websiteData?.registrar_username || websiteData?.registrar_password) && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-3 italic">Login credentials sent via webhook</p>
          )}
        </SectionCard>
      )}

      {/* Website Builder Content */}
      {websiteBuilder && (
        <>
          <div className="space-y-6">
            {/* Hero Section */}
            {websiteBuilder.hero && (
              <SectionCard title="Hero Section">
                <FieldGrid>
                  <DataField label="Headline" value={websiteBuilder.hero.headline} />
                  <DataField label="Tagline" value={websiteBuilder.hero.tagline} />
                  <DataField label="BUY Button Text" value={websiteBuilder.hero.primaryCTA} />
                  <DataField label="SELL Button Text" value={websiteBuilder.hero.secondaryCTA} />
                  {websiteBuilder.hero.logo && (
                    <DataField label="Logo" value={websiteBuilder.hero.logo} type="link" />
                  )}
                  {websiteBuilder.hero.backgroundImage && (
                    <DataField
                      label="Background Image"
                      value={websiteBuilder.hero.backgroundImage}
                      type="link"
                    />
                  )}
                  {websiteBuilder.hero.backgroundVideo && (
                    <DataField
                      label="Background Video"
                      value={websiteBuilder.hero.backgroundVideo}
                      type="link"
                    />
                  )}
                </FieldGrid>
                {websiteBuilder.hero.stats && websiteBuilder.hero.stats.length > 0 && (
                  <div className="mt-4">
                    <SectionLabel>Stats</SectionLabel>
                    <FieldGrid cols={4}>
                      {websiteBuilder.hero.stats.map((stat, idx) => (
                        <StatCard key={idx} value={stat.value ?? ''} label={stat.label ?? ''} />
                      ))}
                    </FieldGrid>
                  </div>
                )}
              </SectionCard>
            )}

            {/* About Section */}
            {websiteBuilder.about && (
              <SectionCard title="About Section">
                <FieldGrid>
                  <DataField label="Company Name" value={websiteBuilder.about.companyName} />
                  {websiteBuilder.about.teamPhoto && (
                    <DataField
                      label="Team Photo"
                      value={websiteBuilder.about.teamPhoto}
                      type="link"
                    />
                  )}
                  {websiteBuilder.about.teamVideo && (
                    <DataField
                      label="Team Video"
                      value={websiteBuilder.about.teamVideo}
                      type="link"
                    />
                  )}
                </FieldGrid>
                {websiteBuilder.about.bio && (
                  <TextBlock label="Bio" text={websiteBuilder.about.bio} />
                )}
              </SectionCard>
            )}

            {/* Featured Locations */}
            {websiteBuilder.featureLocation?.locations &&
              websiteBuilder.featureLocation.locations.length > 0 && (
                <SectionCard title="Featured Locations">
                  <ItemGrid cols={3}>
                    {websiteBuilder.featureLocation.locations.map((loc, idx) => (
                      <ItemCard key={idx}>
                        <div className="font-medium text-gray-900 dark:text-gray-100">{loc.title || '-'}</div>
                        {loc.image && <ViewLink href={loc.image} />}
                      </ItemCard>
                    ))}
                  </ItemGrid>
                </SectionCard>
              )}

            {/* Testimonials */}
            {websiteBuilder.testimonials && (
              <SectionCard title="Testimonials">
                <FieldGrid>
                  {websiteBuilder.testimonials.googleReviewsLink && (
                    <DataField
                      label="Google Reviews Link"
                      value={websiteBuilder.testimonials.googleReviewsLink}
                      type="link"
                    />
                  )}
                  <DataField
                    label="Use Google Reviews"
                    value={websiteBuilder.testimonials.useGoogleReviews}
                    type="boolean"
                  />
                </FieldGrid>
                {websiteBuilder.testimonials.testimonials &&
                  websiteBuilder.testimonials.testimonials.length > 0 && (
                    <ItemGrid>
                      {websiteBuilder.testimonials.testimonials.map((t, idx) => (
                        <ItemCard key={idx}>
                          <div className="font-medium text-gray-900 dark:text-gray-100">{t.name || '-'}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{t.role || '-'}</div>
                          <p className="text-gray-700 dark:text-gray-300 mt-2 text-sm">{t.text || '-'}</p>
                        </ItemCard>
                      ))}
                    </ItemGrid>
                  )}
              </SectionCard>
            )}

            {/* Meet The Team */}
            {websiteBuilder.meetTheTeam && (
              <SectionCard title="Meet The Team">
                <FieldGrid>
                  {websiteBuilder.meetTheTeam.teamRosterLink && (
                    <DataField
                      label="Team Roster Link"
                      value={websiteBuilder.meetTheTeam.teamRosterLink}
                      type="link"
                    />
                  )}
                  <DataField
                    label="Use Team Roster Link"
                    value={websiteBuilder.meetTheTeam.useTeamRosterLink}
                    type="boolean"
                  />
                </FieldGrid>
                {websiteBuilder.meetTheTeam.teamMembers &&
                  websiteBuilder.meetTheTeam.teamMembers.length > 0 && (
                    <ItemGrid>
                      {websiteBuilder.meetTheTeam.teamMembers.map((member, idx) => (
                        <ItemCard key={idx}>
                        <div className="font-medium text-gray-900 dark:text-gray-100">{member.name || '-'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{member.position || '-'}</div>
                        {member.phone && (
                          <div className="text-xs text-gray-600 dark:text-gray-400">{member.phone}</div>
                        )}
                        {member.email && (
                          <div className="text-xs text-yellow-600 dark:text-yellow-400">{member.email}</div>
                        )}
                        {member.license && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">License: {member.license}</div>
                        )}
                        {member.headshot && (
                          <ViewLink href={member.headshot} label="View Headshot" />
                        )}
                        {member.bio && <p className="text-gray-600 dark:text-gray-400 mt-2 text-xs">{member.bio}</p>}
                      </ItemCard>
                    ))}
                  </ItemGrid>
                )}
              </SectionCard>
            )}

            {/* Footer */}
            {websiteBuilder.footer && (
              <SectionCard title="Footer">
                <FieldGrid>
                  <DataField label="Brokerage Name" value={websiteBuilder.footer.brokerageName} />
                  {websiteBuilder.footer.brokerageLogo && (
                    <DataField
                      label="Brokerage Logo"
                      value={websiteBuilder.footer.brokerageLogo}
                      type="link"
                    />
                  )}
                  <DataField label="Copyright Text" value={websiteBuilder.footer.copyrightText} />
                </FieldGrid>

                {/* Contact Info */}
                {websiteBuilder.footer.contactInfo && (
                  <div className="mt-4 pt-3 border-t border-gray-200 dark:border-white/10">
                    <SectionLabel>Contact Information</SectionLabel>
                    <FieldGrid>
                      <DataField
                        label="Phone"
                        value={websiteBuilder.footer.contactInfo.phone}
                        type="phone"
                      />
                      <DataField
                        label="Email"
                        value={websiteBuilder.footer.contactInfo.email}
                        type="email"
                      />
                      <div className="col-span-2">
                        <DataField
                          label="Address"
                          value={websiteBuilder.footer.contactInfo.address}
                        />
                      </div>
                    </FieldGrid>
                  </div>
                )}

                {/* Social Links */}
                {websiteBuilder.footer.socialLinks &&
                  websiteBuilder.footer.socialLinks.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-gray-200 dark:border-white/10">
                      <SectionLabel>Social Links</SectionLabel>
                      <div className="flex flex-wrap gap-2">
                        {websiteBuilder.footer.socialLinks.map((social, idx) => (
                          <SocialLink
                            key={idx}
                            platform={social.platform ?? ''}
                            url={social.url ?? ''}
                          />
                        ))}
                      </div>
                    </div>
                  )}
              </SectionCard>
            )}
          </div>
        </>
      )}

      {/* No content message */}
      {hasNoContent && <p className="text-gray-500 dark:text-gray-400 text-sm">No website data provided</p>}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleConfirmWebsiteCompleted(true)}
        title="Mark Website as Completed"
        message="Are you sure you want to mark this website as completed? This will hide the 'Website Design in Progress' banner for the client."
        confirmLabel="Mark as Completed"
        cancelLabel="Cancel"
        variant="info"
        isLoading={saving}
      />
    </div>
  );
};
